import { FC, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconButton, Stack, SxProps, Typography, useTheme } from '@mui/material';

import DeleteIcon from '@/components/shared/delete-icon';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useBooking } from '@/logic/state';
import ImageBoxFuture from '../shared/image-box-future';
import { LottiePlayer } from '../shared/lottie-player';

type OfferBadgeProps = {
  title: ReactNode;
  subTitle: ReactNode;
  lottie?: unknown;
  icon?: string;
  isShowLeftTicketsNumber?: boolean;
  isDeleteButtonDisabled?: boolean;
  styles?: {
    title?: SxProps;
    subTitle?: SxProps;
    icon?: SxProps;
    container?: SxProps;
    deleteIcon?: SxProps;
  };
  onDelete?: () => void;
};

const OfferBadge: FC<OfferBadgeProps> = ({
  title,
  subTitle,
  lottie,
  icon,
  isShowLeftTicketsNumber,
  isDeleteButtonDisabled,
  styles,
  onDelete,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const booking = useBooking(s => s.booking, shallow);

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      sx={{
        width: '100%',
        padding: pxToRem(20),
        backgroundColor: theme.palette.orange.light,
        borderRadius: '12px',
        ...styles?.container,
      }}
    >
      <Stack direction='row' alignItems='center' spacing={pxToRem(14)} flex='1'>
        {icon && (
          <ImageBoxFuture
            img={icon}
            title={intl.formatMessage({
              id: 'alt.offer_icon',
            })}
            objectFit='contain'
            sx={{
              width: '36px',
              height: '36px',
            }}
          />
        )}
        <LottiePlayer boxSx={{ width: '36px', height: '36px' }} animationData={lottie} />
        <Stack spacing='8px' sx={{ flex: 1 }}>
          <Typography
            variant='h4'
            sx={{
              fontSize: {
                mobile: '12px',
                tablet: pxToRem(16),
              },
              fontWeight: 700,
              color: theme.palette.orange.main,
              ...styles?.title,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize: {
                mobile: '12px',
                tablet: pxToRem(14),
              },
              color: theme.palette.grey[80],
              ...styles?.subTitle,
            }}
          >
            {subTitle}
          </Typography>
        </Stack>
        {isShowLeftTicketsNumber && (
          <Typography
            component='span'
            sx={{
              color: theme.palette.orange.main,
              backgroundColor: theme.palette.grey[0],
              borderRadius: '20px',
              fontSize: {
                mobile: '9px',
                tablet: pxToRem(16),
              },
              paddingX: {
                mobile: '8px',
                tablet: pxToRem(13.5),
              },
              paddingY: {
                mobile: '4px',
                tablet: pxToRem(3),
              },
            }}
          >
            {booking?.offer?.remainedSeats} <FormattedMessage id='booking.tickets_left' />
          </Typography>
        )}
        {onDelete && (
          <IconButton
            id='delete-offer'
            disabled={isDeleteButtonDisabled}
            onClick={onDelete}
            sx={{ color: theme.palette.grey[80], ...styles?.deleteIcon }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

export default OfferBadge;
